import { PairSimProfileSchema, type PairSimProfile } from "../api-contracts/compatibility.js";
import type {
  ProvenanceByParam,
  SimMeasurableParam,
  SimProvenance,
} from "../api-contracts/scenarios.js";
import { originOf } from "./sim-provenance.js";

// FONTE ÚNICA da regra de merge do perfil por par (máquina×produto).
// Consumida pelos espelhos que resolvem parâmetros (worker resolveMachineParams, preview server
// resolveMachine, preview client preview-engine) — a regra existe UMA vez (research D3, risco
// R1.1). O motor Python NÃO resolve nada: recebe valores já mesclados.

/** Subconjunto de um perfil resolvido que o merge por par toca (4 params reais; ciclo deriva de
 * feedRate no motor, logo não é campo). Cada campo é opcional — perfil parcial é válido.*/
export interface PairMergeableProfile {
  feedRateUnH?: number;
  mtbfMin?: number;
  mttrMin?: number;
  qualityTargetPct?: number;
  // `provenance`/`sampleCount` escalares entram para que a guarda de "não sobrepor medido" respeite
  // também snapshots legados (o escalar `measured` projeta em mtbf/mttr via originOf).
  provenance?: SimProvenance;
  sampleCount?: number;
  provenanceByParam?: ProvenanceByParam;
}

/**
 * Campos do perfil por par que o merge REALMENTE mescla. `setupMinutes` está
 * fora de propósito — setup é custo de TRANSIÇÃO (cobrado na troca de produto pelo motor), não
 * uma taxa contínua da máquina; ele viaja em paralelo até o payload, sem tocar a base resolvida.
 */
type PairMergeableField = Extract<
  keyof PairSimProfile,
  "feedRateUnH" | "mtbfMin" | "mttrMin" | "qualityTargetPct"
>;

/** Campo do perfil ↔ chave de proveniência por parâmetro.*/
const FIELD_TO_PARAM: Record<
  PairMergeableField,
  Extract<SimMeasurableParam, "feedRate" | "quality" | "mtbf" | "mttr">
> = {
  feedRateUnH: "feedRate",
  qualityTargetPct: "quality",
  mtbfMin: "mtbf",
  mttrMin: "mttr",
};

/**
 * Aplica o perfil autorado por par sobre uma base já resolvida (medido/similar/premissa).
 * O par vence ONDE está definido, marcando a origem daquele parâmetro como `"product"` no
 * `provenanceByParam`. Campos ausentes no par preservam a base (valor e origem). `pair`
 * null/undefined ⇒ base intacta (retrocompat byte-idêntica).
 *
 * Precedência: o medido-por-produto deve ser resolvido ANTES e entrar na base; este helper
 * só sobrepõe premissa/similar/spec com o autorado (research D4).
 */
export function applyPairProfile<T extends PairMergeableProfile>(
  base: T,
  pair: PairSimProfile | null | undefined
): T {
  if (!pair) return base;

  const merged: T = { ...base };
  const provenance: ProvenanceByParam = { ...(base.provenanceByParam ?? {}) };

  for (const field of Object.keys(FIELD_TO_PARAM) as PairMergeableField[]) {
    const value = pair[field];
    if (value === undefined) continue;
    // Medido do próprio produto é evidência mais forte que o autorado (research D4): não sobrepõe.
    // Usa originOf (regra canônica) p/ cobrir também o escalar legado, não só provenanceByParam.
    const param = FIELD_TO_PARAM[field];
    if (originOf(base, param).provenance === "measured") continue;
    // Cast: `T` pode estreitar o campo (perfil literal); o merge é sempre numérico.
    (merged as PairMergeableProfile)[field] = value;
    provenance[param] = { provenance: "product" };
  }

  merged.provenanceByParam = provenance;
  return merged;
}

/**
 * Lê um `simProfile` de par vindo do banco (Json) ou de um snapshot. Tolerante por contrato:
 * perfil ausente, nulo ou com shape inválido ⇒ `null` (o par simplesmente não tem perfil, o
 * comportamento anterior). Nunca lança — um Json corrompido não pode derrubar uma rodada.
 */
export function readPairProfile(raw: unknown): PairSimProfile | null {
  if (raw == null || typeof raw !== "object") return null;
  const parsed = PairSimProfileSchema.safeParse(raw);
  return parsed.success ? parsed.data : null;
}

/**
 * Perfil autorado do par (máquina×produto) para o produto em questão, dentro da lista de
 * compatibilidades de UMA máquina. Sem produto, sem entrada ou sem perfil ⇒ `null`.
 * Fonte única do "achar o par certo" nos espelhos (worker, preview server, preview client).
 */
export function pairProfileFor(
  compatibility: Array<{ productId: string; simProfile?: unknown }> | null | undefined,
  productId: string | null | undefined
): PairSimProfile | null {
  if (!compatibility?.length || !productId) return null;
  const entry = compatibility.find((c) => c.productId === productId);
  return readPairProfile(entry?.simProfile);
}
